import { useQuery } from '@tanstack/react-query';
import { useFetch } from '@/shared/hooks/useFetch';
import { useMutate } from '@/shared/hooks/useMutate';
import { queryKeys } from '@/shared/api/queryKeys';
import {
  apiSplitsDelete,
  apiSplitsDetailGet,
  apiSplitsListGet,
  apiSplitsPendingCount,
  apiSplitsSave,
} from '@/entities/splits/modules/api/splits';
import type { ApiSplitsListParams, ApiSplitsSaveBody } from '@/shared/types/api/splits';

export const useSplitsList = (params: ApiSplitsListParams) =>
  useFetch({
    api: () => apiSplitsListGet(params),
    keywords: queryKeys.splits.list(params),
    staleTime: 30_000,
  });

export const useSplitDetail = (id: string | undefined) =>
  useFetch({
    api: () => apiSplitsDetailGet(id ?? ''),
    keywords: queryKeys.splits.detail(id ?? ''),
    enabled: Boolean(id),
    staleTime: 15_000,
  });

export const useSaveSplit = (id: string) =>
  useMutate({
    api: (body: ApiSplitsSaveBody) => apiSplitsSave(id, body),
    invalidateKeys: [
      queryKeys.splits.all,
      queryKeys.splits.detail(id),
      queryKeys.splits.pendingCount,
      // Split approval moves PINFL limit usage from the owner to recipients.
      queryKeys.pinfl.all,
    ],
  });

export const useDeleteSplit = (id: string) =>
  useMutate({
    api: () => apiSplitsDelete(id),
    invalidateKeys: [queryKeys.splits.all, queryKeys.splits.pendingCount, queryKeys.pinfl.all],
  });

export const useSplitsPendingCount = (enabled = true) =>
  useQuery({
    queryKey: queryKeys.splits.pendingCount,
    queryFn: apiSplitsPendingCount,
    enabled,
    refetchInterval: false,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  });
